import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useReadProjectGroupsItemSheet,
  useReadProjectGroupsItemSheetLatest,
} from "@/libs/react-query/hooks/useItemsheetsQuery";
import { ItemSheetOriginal } from "@/types/itemsheet";
import {
  useSelectedProjectInfo,
  useSetSelectedOriginalInfo,
} from "./useTabStore";
import { ProjectType } from "@/consts/Project";
import { useReadProjectGroups } from "@/libs/react-query/hooks/useProjectQuery";
import { useTableStore } from "./useTableStore";

export function useProjectEstimate(selectedOriginal: ItemSheetOriginal) {
  const { group_id = "" } = useParams();

  const [tab, setTab] = useState<string>("");
  const [budgetAmount, setBudgetAmount] = useState<number>(0);
  const [basisAmount, setBasisAmount] = useState<number | undefined>(undefined);

  const setRows = useTableStore((state) => state.setRows);

  const setSelectedOriginalInfo = useSetSelectedOriginalInfo();
  const selectedProjectInfo = useSelectedProjectInfo();
  const selectedProjectId = selectedProjectInfo?.projectId ?? "";
  const selectedProjectType =
    selectedProjectInfo?.projectType ?? ProjectType.PROJ;
  const selectedLatestSheetId = selectedProjectInfo?.latestSheetId ?? "";

  const { data: projectGroupData } = useReadProjectGroups(group_id);
  const { data: latestsData, isLoading: latestsLoading } =
    useReadProjectGroupsItemSheet(group_id, {
      project_id: selectedProjectId,
      project_type: selectedProjectType,
      original_id: selectedOriginal.id,
    });
  const latests = useMemo(() => latestsData?.latests ?? [], [latestsData]);

  const { data: latestsSheetData, isLoading: latestSheetLoading } =
    useReadProjectGroupsItemSheetLatest(group_id, tab, {
      project_id: selectedProjectId,
      project_type: selectedProjectType,
      original_id: selectedOriginal.id,
    });
  const latestSheet = latestsSheetData?.latest;

  const sumPurchaseAmount = useMemo(() => {
    const rows = latestsSheetData?.latest?.data?.data;
    if (!Array.isArray(rows)) return 0;

    return rows.reduce((acc, row) => {
      return Math.round(acc + (row.total_cost ?? 0) * 1.1); // 부가세 포함된 가격
    }, 0);
  }, [latestsSheetData]);

  const sumSalesAmount = useMemo(() => {
    const rows = latestsSheetData?.latest?.data?.data;
    if (!Array.isArray(rows)) return 0;

    return rows.reduce((acc, row) => {
      const value = parseFloat(String(row.total_list_price));
      return acc + (isNaN(value) ? 0 : value) * 1.1;
    }, 0);
  }, [latestsSheetData]);

  useEffect(() => {
    if (!tab || selectedProjectInfo?.latestSheetId === tab) return;

    setSelectedOriginalInfo({ latestSheetId: tab });
  }, [tab, selectedProjectInfo?.latestSheetId, setSelectedOriginalInfo]);

  useEffect(() => {
    if (!projectGroupData?.projects || !selectedProjectId) return;

    const project = projectGroupData.projects.find(
      (item) => item.project_id === selectedProjectId
    );

    if (project) {
      setBudgetAmount(project.budget_amount);
      setBasisAmount(project.basis_amount);
    }
  }, [projectGroupData, selectedProjectId]);

  useEffect(() => {
    if (latests.length > 0) {
      const confirmedTab = latests.find((latest) => latest.is_finalized);
      const exists = latests.some((item) => item.id === tab);

      if (confirmedTab && tab !== confirmedTab.id) {
        setTab(confirmedTab.id);
      } else if (!exists) {
        setTab(latests[0].id);
      }
    } else {
      setTab("");
    }
  }, [latests]);

  useEffect(() => {
    if (latestsSheetData?.latest) {
      setRows(latestsSheetData.latest?.data);
    }
  }, [latestsSheetData, setRows]);

  return {
    tab,
    setTab,
    budgetAmount,
    basisAmount,
    latests,
    latestsSheetData: latestSheet,
    isLoading: latestsLoading || latestSheetLoading,
    sumPurchaseAmount,
    sumSalesAmount,
  };
}
